import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { AppStateFacade } from '@app-state';
import { IDeploymentSettingsFormValue } from '@components/ui-forms/deployment/ui-deployment-settings.component';
import {
    INugetDeploymentSettings,
    NugetType,
} from '@models';
import { DeploymentAuthenticationType } from '@services';
import UiValidators, {
    UI_PASSWORD_MAX_LENGTH,
    UI_URL_MAX_LENGTH,
    UI_USERNAME_MAX_LENGTH,
} from '@validators';

import cloneDeep from 'lodash-es/cloneDeep';
import {
    BehaviorSubject,
    Observable,
    Subject,
} from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    takeUntil,
    tap,
} from 'rxjs/operators';

export type DeploymentType = 'internal' | 'external';
export type FeedSource = 'host' | 'tenant';

export enum DeploymentGeneralSettingsControl {
    useSharedFeed = 'useSharedFeed',
    allowTenantPublish = 'allowTenantPublish',
    isInternalDeployment = 'isInternalDeployment',
    externalDeploymentUrl = 'externalDeploymentUrl',
    selectedAuthenticationType = 'selectedAuthenticationType',
    authenticationTypes = 'authenticationTypes',
}

export enum DeploymentAuthenticationTypeControl {
    apiKey = 'apiKey',
    basicAuthUsername = 'basicAuthUsername',
    basicAuthPassword = 'basicAuthPassword',
    secure = 'secure',
}

@Component({
    selector: 'ui-nuget-deployment-settings',
    templateUrl: './ui-nuget-deployment-settings.component.html',
    styleUrls: ['../ui-setting-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiNugetDeploymentSettingsComponent
    implements OnInit, OnDestroy, OnChanges {

    @Input()
    deploymentSettings!: INugetDeploymentSettings;

    @Input()
    nugetType!: NugetType;

    public DeploymentAuthenticationType = DeploymentAuthenticationType;
    public DeploymentGeneralSettingsControl = DeploymentGeneralSettingsControl;
    public DeploymentAuthenticationTypeControl = DeploymentAuthenticationTypeControl;

    public formGroup: FormGroup;

    public formChanges$!: Observable<IDeploymentSettingsFormValue>;
    public feedSourceChanges$!: BehaviorSubject<FeedSource>;
    public deploymentTypeChanges$!: BehaviorSubject<DeploymentType>;
    public authenticationTypeChanges$!: BehaviorSubject<DeploymentAuthenticationType>;

    private _originalDeploymentSettings!: INugetDeploymentSettings;
    private _destroyed$ = new Subject();

    private _isInitialized = false;

    constructor(
        private _fb: FormBuilder,
        private _appState: AppStateFacade,
    ) {
        this.formGroup = this._fb.group({});
    }

    public get isInitialized() {
        return this._isInitialized;
    }

    public get authenticationTypesFormGroup() {
        return this.formGroup.get(DeploymentGeneralSettingsControl.authenticationTypes) as FormGroup;
    }

    public get authenticationTypeFormGroupNames() {
        return Object.keys(this.authenticationTypesFormGroup.controls);
    }

    public get authenticationTypeFormGroupNamesOrdered() {
        return this._getAuthenticationTypes(
            this.nugetType,
            this.deploymentTypeChanges$.value,
        );
    }

    public get showTenantPublishOptions() {
        return this._appState.user.loggedInAsHost &&
            this.nugetType === NugetType.Libraries;
    }

    public getAuthenticationTypeFormGroupByName(name: string) {
        return this.authenticationTypesFormGroup.get(name) as FormGroup;
    }

    public get hasMultipleFeedSources() {
        return this._appState.user.loggedInAsHost ?
            false :
            this._originalDeploymentSettings.useSharedFeed != null;
    }

    private get _isSingleAuthenticationType() {
        return this.authenticationTypeFormGroupNames.length === 1;
    }

    private get _initialDeploymentType() {
        return this._originalDeploymentSettings.isInternalDeployment ? 'internal' : 'external';
    }
    private get _initialFeedSource() {
        return this.hasMultipleFeedSources && this._originalDeploymentSettings.useSharedFeed ? 'host' : 'tenant';
    }

    private get _initialSelectedAuthenticationType() {
        return this._originalDeploymentSettings.selectedAuthenticationType as DeploymentAuthenticationType;
    }

    private set _showDeploymentUrlField(isExternalDeployment: boolean) {
        if (isExternalDeployment) {
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.externalDeploymentUrl,
                new FormControl(
                    this._originalDeploymentSettings.externalDeploymentUrl,
                    Validators.compose([
                        Validators.required,
                        UiValidators.httpUrl(true),
                        Validators.maxLength(UI_URL_MAX_LENGTH),
                    ]),
                ),
            );
        } else {
            this.formGroup.removeControl(DeploymentGeneralSettingsControl.externalDeploymentUrl);
        }
    }

    private set _enableAuthenticationTypeFormGroup(selectedAuthenticationType: DeploymentAuthenticationType) {
        this.authenticationTypeFormGroupNames.forEach(
            authenticationTypeFormGroupName => {
                const authenticationTypeFormGroup = this.getAuthenticationTypeFormGroupByName(authenticationTypeFormGroupName);
                if (authenticationTypeFormGroupName === selectedAuthenticationType) {
                    authenticationTypeFormGroup.enable();
                } else {
                    this._resetAuthenticationTypeFormGroupControls(authenticationTypeFormGroup);
                    authenticationTypeFormGroup.disable();
                }
            });
    }

    private set _authenticationTypeFormGroups(deploymentType: DeploymentType) {
        this._showDeploymentUrlField = deploymentType === 'external';

        const authenticationTypes = this._getAuthenticationTypes(this.nugetType, deploymentType);

        this._removeUnavailableAuthenticationTypeFormGroups(authenticationTypes);
        this._addMissingAuthenticationTypeFormGroups(authenticationTypes);

        if (this._isSingleAuthenticationType) {
            const [onlyAuthenticationType] = authenticationTypes;
            this.formGroup.get(DeploymentGeneralSettingsControl.selectedAuthenticationType)!.setValue(onlyAuthenticationType);
        }

        this._enableAuthenticationTypeFormGroup = this.formGroup.get(DeploymentGeneralSettingsControl.selectedAuthenticationType)!.value;
    }


    private set _showDeploymentGeneralSettings(feedSource: FeedSource) {
        if (feedSource === 'tenant') {
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.isInternalDeployment,
                new FormControl(this._originalDeploymentSettings.isInternalDeployment),
            );
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.selectedAuthenticationType,
                new FormControl(this._originalDeploymentSettings.selectedAuthenticationType),
            );
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.authenticationTypes,
                this._fb.group({}),
            );
            this.formGroup.setValidators(this._authenticationTypeValidator);
        } else {
            this.formGroup.setValidators(null);
            [
                DeploymentGeneralSettingsControl.isInternalDeployment,
                DeploymentGeneralSettingsControl.externalDeploymentUrl,
                DeploymentGeneralSettingsControl.selectedAuthenticationType,
                DeploymentGeneralSettingsControl.authenticationTypes,
            ].forEach(controlName =>
                this.formGroup.removeControl(controlName),
            );
        }
    }

    ngOnInit() {
        if (this.hasMultipleFeedSources) {
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.useSharedFeed,
                new FormControl(this._originalDeploymentSettings.useSharedFeed),
            );
        }

        if (this.showTenantPublishOptions) {
            this.formGroup.addControl(
                DeploymentGeneralSettingsControl.allowTenantPublish,
                new FormControl(this._originalDeploymentSettings.allowTenantPublish),
            );
        }

        this.feedSourceChanges$ = new BehaviorSubject<FeedSource>(this._initialFeedSource);
        this.deploymentTypeChanges$ = new BehaviorSubject<DeploymentType>(this._initialDeploymentType);
        this.authenticationTypeChanges$ = new BehaviorSubject(this._initialSelectedAuthenticationType);

        this.feedSourceChanges$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(
                feedSource => this._onFeedSourceChange(feedSource),
            );

        this.deploymentTypeChanges$
            .pipe(
                filter(() => this.feedSourceChanges$.value !== 'host'),
                takeUntil(this._destroyed$),
            ).subscribe(
                deploymentType => this._authenticationTypeFormGroups = deploymentType,
            );

        this.authenticationTypeChanges$
            .pipe(
                // https://github.com/angular/angular/issues/22556 - if the form is disabled during init, it remains enabled
                // TODO: remove the workaround when the issue gets fixed
                delay(0),
                // need the _isInitialized variable because the authentication type forms are disabled with a delay(0),
                // so they remain invalid until then, which causes a flash with the invalid warning triangle
                // in the Deployment tab at load time
                tap(() => this._isInitialized = true),
                filter(() => this.feedSourceChanges$.value !== 'host'),
                takeUntil(this._destroyed$),
            ).subscribe(
                authenticationType => this._enableAuthenticationTypeFormGroup = authenticationType,
            );

        this.formChanges$ =
            this.formGroup.valueChanges
                .pipe(
                    distinctUntilChanged(),
                    takeUntil(this._destroyed$),
                );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['deploymentSettings']) {
            this._originalDeploymentSettings = cloneDeep(this.deploymentSettings);
        }
    }

    ngOnDestroy() {
        this._destroyed$.next();
    }

    public onFeedSourceRadioChange(event: MatRadioChange) {
        this.feedSourceChanges$.next(event.value ? 'host' : 'tenant');
    }

    public onDeploymentTypeRadioChange(event: MatRadioChange) {
        this.deploymentTypeChanges$.next(event.value ? 'internal' : 'external');
    }

    public onAuthenticationTypeRadioChange(event: MatRadioChange) {
        if (event.value) {
            this.authenticationTypeChanges$.next(event.value);
        }
    }

    private _onFeedSourceChange(feedSource: FeedSource) {
        this._showDeploymentGeneralSettings = feedSource;

        if (feedSource === 'tenant') {
            this._authenticationTypeFormGroups = this._initialDeploymentType;
        }
    }

    private _resetAuthenticationTypeFormGroupControls(authenticationTypeFormGroup: FormGroup) {
        Object.keys(authenticationTypeFormGroup.controls).forEach(
            controlName => {
                authenticationTypeFormGroup
                    .get(controlName)!
                    .setValue(this._originalDeploymentSettings[controlName as keyof INugetDeploymentSettings]);
            },
        );
    }

    private _removeUnavailableAuthenticationTypeFormGroups(authenticationTypes: DeploymentAuthenticationType[]) {
        this.authenticationTypeFormGroupNames
            .filter(
                authenticationTypeFormGroupName =>
                    !authenticationTypes.includes(authenticationTypeFormGroupName as DeploymentAuthenticationType),
            ).forEach(
                authenticationTypeFormGroupName => {
                    const authenticationTypeFormGroup = this.getAuthenticationTypeFormGroupByName(authenticationTypeFormGroupName);
                    this._resetAuthenticationTypeFormGroupControls(authenticationTypeFormGroup);
                    this.authenticationTypesFormGroup.removeControl(authenticationTypeFormGroupName);
                },
            );
    }

    private _addMissingAuthenticationTypeFormGroups(authenticationTypes: DeploymentAuthenticationType[]) {
        authenticationTypes
            .filter(
                authenticationType =>
                    !this.authenticationTypeFormGroupNames.includes(authenticationType),
            )
            .forEach(
                authenticationType => {
                    const formGroup = this._getAuthenticationTypeFormGroup(authenticationType);
                    this.authenticationTypesFormGroup.addControl(authenticationType, formGroup);
                });
    }

    private _authenticationTypeValidator: ValidatorFn = (form: AbstractControl) => {
        const validAuthenticationTypes = this._getAuthenticationTypes(
            this.nugetType,
            this.deploymentTypeChanges$.value,
        );
        const selectedAuthenticationType = form.get(DeploymentGeneralSettingsControl.selectedAuthenticationType)!.value;

        if (validAuthenticationTypes.includes(selectedAuthenticationType)) {
            return null;
        }

        return {
            invalidSelection: true,
        };
    }

    private _getAuthenticationTypes(
        nugetType: NugetType,
        deploymentType: DeploymentType,
    ) {
        switch (nugetType) {
            case NugetType.Packages:
                return deploymentType === 'internal' ?
                    [
                        DeploymentAuthenticationType.secure,
                        DeploymentAuthenticationType.apiKey,
                    ] : [
                        DeploymentAuthenticationType.apiKey,
                        DeploymentAuthenticationType.basicAuth,
                    ];
            case NugetType.Libraries:
                return deploymentType === 'internal' ?
                    [
                        DeploymentAuthenticationType.secure,
                        DeploymentAuthenticationType.apiKey,
                    ] : [
                        DeploymentAuthenticationType.apiKey,
                    ];
        }
    }

    private _getAuthenticationTypeFormGroup(authenticationType: DeploymentAuthenticationType) {
        switch (authenticationType) {
            case DeploymentAuthenticationType.apiKey:
                return this._fb.group({
                    [DeploymentAuthenticationTypeControl.apiKey]: [
                        this._originalDeploymentSettings.apiKey,
                        Validators.compose([
                            UiValidators.whitespace,
                        ]),
                    ],
                });
            case DeploymentAuthenticationType.basicAuth:
                return this._fb.group({
                    [DeploymentAuthenticationTypeControl.basicAuthUsername]: [
                        this._originalDeploymentSettings.basicAuthUsername,
                        Validators.compose([
                            Validators.required,
                            UiValidators.whitespace,
                            Validators.maxLength(UI_USERNAME_MAX_LENGTH),
                        ]),
                    ],
                    [DeploymentAuthenticationTypeControl.basicAuthPassword]: [
                        this._originalDeploymentSettings.basicAuthPassword,
                        Validators.compose([
                            Validators.required,
                            UiValidators.whitespace,
                            Validators.maxLength(UI_PASSWORD_MAX_LENGTH),
                        ]),
                    ],
                });
            case DeploymentAuthenticationType.secure:
                return this._fb.group({});
        }
    }
}
